import { FC, useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { AuthenticatedShell, ProtectedRoute, PublicRoute } from '@/components';
import { PRIVATE_PATH, PUBLIC_PATH, ALLOWED_ROLES } from '@/constants';

import {
  BookingSheet,
  CreativeLibrary,
  Dashboard,
  Login,
  MediaPlan,
  NotFound,
  RateCards,
  UserSettings,
  EU6Calculator,
  TraderSheet,
  Reports,
  MediaPlanList,
  CalculatorRateCard,
  CustomPackages,
  CustomPackageDetail,
  TraderControls,
  TraderControlsChannels,
  TraderControlsDspFees,
  TraderControlsKpis,
  TraderControlsTradingDeals,
  Forecasting,
  ThreeMonthForecast,
  CampaignMargins,
  Templates,
  RaiseIo,
  Io
} from '@/pages';
import { USER_ROLE } from '@skyrise-eng/types';
import { useAppDispatch } from './hooks/useAppDispatch';
import { auth } from './services/firebase';
import { signIn, signOut } from './store/auth/actions';
import MediaPlanDetails from './pages/MediaPlanDetails';

const App: FC = () => {
  const dispatch = useAppDispatch();

  /* The one place a Firebase user becomes an app user: on load (a restored
     session), after the sign-in popup, and when the session ends elsewhere
     (another tab signing out, a revoked account).

     The unsubscribe is returned as the cleanup, not called — calling it
     detached the listener straight away, so only the first event was ever
     seen and a sign-out in another tab never reached this one. */
  useEffect(
    () =>
      auth.onAuthStateChanged((firebaseUser) => {
        if (firebaseUser) {
          dispatch(signIn(firebaseUser));
        } else {
          dispatch(signOut());
        }
      }),
    [dispatch]
  );

  return (
    <Routes>
      <Route element={<AuthenticatedShell />}>
        <Route element={<ProtectedRoute allowedRoles={ALLOWED_ROLES.MEDIA_PLAN.READ} />}>
          <Route path={PRIVATE_PATH.MEDIA_PLAN} element={<MediaPlan />}>
            <Route index element={<MediaPlanList />} />
            <Route path="templates" element={<Templates />} />
          </Route>
          <Route path={`${PRIVATE_PATH.MEDIA_PLAN}/:id`} element={<MediaPlanDetails />} />
          <Route path={PRIVATE_PATH.RAISE_IO} element={<RaiseIo />} />
          <Route path={PRIVATE_PATH.IO} element={<Io />} />
        </Route>
        <Route element={<ProtectedRoute allowedRoles={ALLOWED_ROLES.RATE_CARD.READ} />}>
          <Route path={PRIVATE_PATH.RATE_CARD} element={<RateCards />} />
        </Route>
        <Route element={<ProtectedRoute allowedRoles={ALLOWED_ROLES.USER_SETTINGS.READ} />}>
          <Route path={PRIVATE_PATH.USER_SETTINGS} element={<UserSettings />} />
        </Route>
        <Route element={<ProtectedRoute allowedRoles={ALLOWED_ROLES.CUSTOM_PACKAGE.READ} />}>
          <Route path={PRIVATE_PATH.CUSTOM_PACKAGE} element={<CustomPackages />} />
          <Route path={`${PRIVATE_PATH.CUSTOM_PACKAGE}/:id`} element={<CustomPackageDetail />} />
        </Route>
        <Route element={<ProtectedRoute allowedRoles={ALLOWED_ROLES.EU6_CALCULATOR.READ} />}>
          <Route path={PRIVATE_PATH.EU6_CALCULATOR} element={<EU6Calculator />} />
        </Route>
        <Route element={<ProtectedRoute allowedRoles={ALLOWED_ROLES.CALCULATOR_RATE_CARD.READ} />}>
          <Route path={PRIVATE_PATH.CALCULATOR_RATE_CARD} element={<CalculatorRateCard />} />
        </Route>
        <Route element={<ProtectedRoute allowedRoles={ALLOWED_ROLES.BOOKING_SHEET.READ} />}>
          <Route path={PRIVATE_PATH.BOOKING_SHEET} element={<BookingSheet />} />
        </Route>
        <Route element={<ProtectedRoute allowedRoles={ALLOWED_ROLES.TRADER_SHEET.READ} />}>
          <Route path={PRIVATE_PATH.TRADER_SHEET} element={<TraderSheet />} />
        </Route>
        <Route element={<ProtectedRoute allowedRoles={ALLOWED_ROLES.CREATIVE_LIBRARY.READ} />}>
          <Route path={PRIVATE_PATH.CREATIVE_LIBRARY} element={<CreativeLibrary />} />
        </Route>
        <Route element={<ProtectedRoute allowedRoles={ALLOWED_ROLES.DASHBOARD.READ} />}>
          <Route path={PRIVATE_PATH.DASHBOARD} element={<Dashboard />} />
          <Route path={PRIVATE_PATH.REPORTS} element={<Reports />} />
        </Route>
        <Route element={<ProtectedRoute allowedRoles={ALLOWED_ROLES.FORECASTING.READ} />}>
          <Route path={PRIVATE_PATH.FORECASTING} element={<Forecasting />} />
          <Route path={PRIVATE_PATH.FORECASTING_THREE_MONTH} element={<ThreeMonthForecast />} />
          <Route path={PRIVATE_PATH.FORECASTING_MARGINS} element={<CampaignMargins />} />
        </Route>
        <Route element={<ProtectedRoute allowedRoles={[USER_ROLE.ADMIN, USER_ROLE.SUPER_ADMIN]} />}>
          <Route path={PRIVATE_PATH.TRADER_CONTROLS} element={<TraderControls />}>
            <Route index element={<Navigate to="channels" replace />} />
            <Route path="channels" element={<TraderControlsChannels />} />
            <Route path="dsp-fees" element={<TraderControlsDspFees />} />
            <Route path="kpis" element={<TraderControlsKpis />} />
            <Route path="trading-deals" element={<TraderControlsTradingDeals />} />
          </Route>
        </Route>
      </Route>
      <Route element={<PublicRoute />}>
        <Route path="/" element={<Login />} />
        <Route path={PUBLIC_PATH.LOGIN} element={<Login />} />
      </Route>
      <Route path="/*" element={<NotFound />} />
    </Routes>
  );
};
export default App;
